import React, { useState, useEffect } from "react";
import { FiX, FiTrash2, FiPlus, FiMoreVertical } from "react-icons/fi";
import "../styles/EditField.css";

const EditFieldModal = ({ open, field, onClose, onSave }) => {
    const [name, setName] = useState("");
    const [type, setType] = useState("Dropdown");
    const [optionInput, setOptionInput] = useState("");
    const [options, setOptions] = useState([]);
    const [originalOptions, setOriginalOptions] = useState([]);
    const [error, setError] = useState("");
    const [saveerror, setSaveError] = useState("");
    // eslint-disable-next-line 
    const [loading, setLoading] = useState(false);
    const [deletedIds, setDeletedIds] = useState([])
    const [draggingIndex, setDraggingIndex] = useState(null);

    useEffect(() => {
        if (field) {
            setName(field.name || "");
            setType(field.type || "Dropdown");

            // ✅ fetch options when modal opens (only when a key is present)
            if (field.key) {
                fetchOptions(field.key);
            } else {
                // for fields without remote key, reset options to empty
                setOptions([]);
                setOriginalOptions([]);
            }
        }
    }, [field]);

    const fetchOptions = async (fieldKeyName) => {
        try {
            setLoading(true);

            const apiUrl =
                process.env.REACT_APP_API_URL +
                `/admin/master-data/${fieldKeyName}`;

            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                    Accept: "application/json",
                },
            });

            const res = await response.json();
            console.log(res.data);
            // ✅ map API response Name → options
            console.log("res.data", res.data);
            const mappedOptions = (res.data || [])
                .map((item) => {

                    const label =
                        item?.Label?.trim() ||
                        item?.Name?.trim() ||
                        item?.label?.trim();

                    const value =
                        item?.Value?.trim() ||
                        item?.value?.trim() ||
                        item?.Name?.trim(); // fallback

                    if (!label || !value) return null;

                    return {
                        id: item.id ?? null,
                        sortOrder: Number(item.SortOrder) || 0,
                        label,
                        value
                    };
                })
                .filter(Boolean)
                .sort((a, b) => a.sortOrder - b.sortOrder);
            console.log("mappedOptions", mappedOptions);
            setOptions(mappedOptions);
            setOriginalOptions(mappedOptions);
        } catch (error) {
            console.error("Error fetching options:", error);
        } finally {
            setLoading(false);
        }
    };


    if (!open || !field) return null;


    const addOption = () => {
        const val = optionInput.trim();

        if (!val) return;

        // ✅ check duplicate (case-insensitive on value)
        const exists = options.some(
            (opt) => opt.label.toLowerCase() === val.toLowerCase()
        );

        if (exists) {
            setError("Option already exists");
            return;
        }

        // ✅ new option object
        const newOption = {
            id: null, // new item (no DB id yet)
            sortOrder: options.length + 1,
            label: val,
            value: val
        };

        setOptions([...options, newOption]);
        setOptionInput("");
        setError("");
    };

    const deleteOption = (index) => {
        const removed = options[index];

        if (removed?.id != null) {
            setDeletedIds((prev) =>
                prev.includes(removed.id) ? prev : [...prev, removed.id]
            );
        }

        setOptions(options.filter((_, i) => i !== index));
    };

    // DRAG & DROP handlers (HTML5 drag API)
    const handleDragStart = (e, index) => {
        setDraggingIndex(index);
        // set transfer so drop event fires in some browsers
        try {
            e.dataTransfer.setData("text/plain", index.toString());
        } catch (err) {
            // ignore
        }
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = draggingIndex ?? Number(e.dataTransfer.getData("text/plain"));
        if (sourceIndex == null || isNaN(sourceIndex)) return;

        if (sourceIndex === targetIndex) {
            setDraggingIndex(null);
            return;
        }

        const newOptions = Array.from(options);
        const [moved] = newOptions.splice(sourceIndex, 1);
        newOptions.splice(targetIndex, 0, moved);

        // update sortOrder according to new UI order
        const normalized = newOptions.map((opt, i) => ({ ...opt, sortOrder: i + 1 }));
        setOptions(normalized);
        setDraggingIndex(null);
    };

    const handleDragEnd = () => {
        setDraggingIndex(null);
    };


    const handleSave = async() => {
        // onSave({
        //     ...field,
        //     name,
        //     type,
        //     options,
        // });
        // onClose();


        try {


            // If field has no remote key, just call onSave locally
            if (!field.key) {
                onSave({
                    ...field,
                    name,
                    type,
                    options,
                });
                setSaveError("Saved locally");
                return;
            }

            const hasChanges =  JSON.stringify(options) !== JSON.stringify(originalOptions);
            if(!hasChanges) {
                console.log("No changes to save");
                setSaveError("No changes to save");
                return;
            }

            const payload = {
                data: options.map((opt, index) => ({
                    id: opt.id ?? null,
                    value: opt.value?.trim() || "",
                    label: opt.label?.trim() || "",
                    sortOrder: index + 1
                })),
                deletedIds: deletedIds || []
            };

            const apiUrl = process.env.REACT_APP_API_URL + `/admin/master-data/${field.key}`;

            const response = await fetch(apiUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                    Accept: "application/json",
                },
                body: JSON.stringify(payload),
            });

            const res = await response.json();

            if (res.status) {
                console.log("✅ Saved successfully");
                setSaveError("Saved successfully");

                // ✅ call parent save if needed
                onSave({
                    ...field,
                    name,
                    type,
                    options,
                });
            } else {
                console.error("❌ Save failed", res);
            }
        } catch (error) {
            console.error("Error saving data:", error);
        }

    };

    return (
        <div className="modal-overlay">
            <div className="edit-modal">

                {/* HEADER */}
                <div className="modal-header">
                    <h3 style={{ fontFamily: '"Futura PT", sans-serif', fontSize: '16px', fontWeight: 400, color: '#178b96' }}>EDIT FIELD</h3>
                    <button onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                {/* BODY */}
                <div className="modal-body">
                    <div className="modal-body-content">

                    <label>FIELD NAME</label>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                            className="form-control"
                        readOnly
                    />

                    <label>FIELD TYPE</label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="form-control"
                        disabled
                    >
                        <option>Text</option>
                        <option>Phone</option>
                        <option>Date</option>
                        <option>Time</option>
                        <option>Dropdown</option>
                        <option>Checkbox</option>
                    </select>

                

                    <label>ADD OPTION</label>

                    <div className="add-option">

                            <input
                                value={optionInput}
                                onChange={(e) => {
                                    setOptionInput(e.target.value);
                                    setError(""); // clear while typing
                                    setSaveError("");
                                }}
                                className={`form-control ${error ? "input-error" : ""}`}
                            />


                        <button onClick={addOption}>
                            <FiPlus />
                        </button>
                        </div>

                        {/* ✅ Error Message */}
                        {error && <p className="error-text">{error}</p>}


                    <label>CURRENT OPTIONS</label>

                    <div className="option-list">
                        {options.map((item, index) => {
                            const isDragging = draggingIndex === index;
                            return (
                                <div
                                    key={index}
                                    className={`option-item ${isDragging ? "dragging" : ""}`}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                >
                                    <div className="option-left">
                                        <FiMoreVertical />
                                        <span>{item.label}</span>
                                    </div>

                                    <button
                                        className="delete-btn"
                                        onClick={() => deleteOption(index)}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                    </div>
                </div>

                {/* FOOTER */}
                <br />
                <div className="modal-footer">
                
                    <button onClick={onClose}>
                        Cancel
                    </button>

                    <button onClick={handleSave}>
                        Save Changes
                    </button>
                </div>

                {/* ✅ Error Message */}
                {saveerror && <p className="error-text">{saveerror}</p>}

            </div>
        </div>
    );
};

export default EditFieldModal;